import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  getSportsTicker,
  getStats,
  getSubmissions,
  getSurveyScores,
  updateSubmission,
} from '../api/client.js';
import Celebration from '../components/Celebration.jsx';
import FilterBar from '../components/FilterBar.jsx';
import Layout from '../components/Layout.jsx';
import MiniPieChart from '../components/MiniPieChart.jsx';
import MiniScoreCircle from '../components/MiniScoreCircle.jsx';
import { QUICK_FILTERS } from '../components/Sidebar.jsx';
import SubmissionTable from '../components/SubmissionTable.jsx';
import { SportsTickerBar } from '../components/SportsTicker.jsx';
import usePolling from '../hooks/usePolling.js';
import { useAuth } from '../context/AuthContext.jsx';
import { loadColumnPrefs, saveColumnPrefs } from '../utils/columnPrefs.js';

const PAGE_SIZE = 25;
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

// Date-range presets for the stat cards + pie charts. "All Time" sends no
// date params (full history, the original dashboard behavior).
const DATE_RANGES = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7d', label: 'Last 7 Days', days: 6 },
  { key: '30d', label: 'Last 30 Days', days: 29 },
  { key: 'all', label: 'All Time', days: null },
];

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Resolve a preset key to { start_date, end_date } ISO strings (undefined for All Time). */
function resolveRange(key) {
  const preset = DATE_RANGES.find((r) => r.key === key);
  if (!preset || preset.days === null) return { start_date: undefined, end_date: undefined };
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - preset.days);
  return { start_date: isoDate(start), end_date: isoDate(end) };
}

// Centier brand palette for the neutral-category pies (intent / accomplished).
const CHART_COLORS = ['#002855', '#FFAE1E', '#4A90D9', '#6DB33F', '#C0392B', '#8E44AD'];
const SENTIMENT_LABELS = {
  positive: 'Positive',
  neutral: 'Neutral',
  frustrated: 'Frustrated',
  urgent: 'Urgent',
};
// Semantic colors so sentiment slices read consistently.
const SENTIMENT_COLORS = {
  positive: '#6DB33F', // green
  neutral: '#FFAE1E', // Centier gold
  frustrated: '#C0392B', // red
  urgent: '#002855', // Centier navy
};

function readFiltersFromSearch(sp) {
  return {
    status: sp.get('status') || undefined,
    severity: sp.get('severity') || undefined,
    search: sp.get('search') || undefined,
    include_system: sp.get('include_system') === 'true' || undefined,
    assigned_to_name: sp.get('assigned_to_name') || undefined,
    unassigned: sp.get('unassigned') === 'true' || undefined,
    custom_assignee: sp.get('custom_assignee') === 'true' || undefined,
    follow_up: sp.get('follow_up') === 'true' || undefined,
  };
}

/** Return the quick-filter key that matches the current filter state, if any. */
function computeActiveQuickFilter(filters) {
  if (filters.severity === 'critical') return 'critical';
  if (filters.severity === 'high') return 'high';
  if (filters.unassigned) return 'unassigned';
  if (filters.follow_up) return 'follow_up';
  if (filters.status === 'resolved') return 'resolved';
  return null;
}

function readPagingFromSearch(sp) {
  const page = parseInt(sp.get('page') || '1', 10);
  const sortBy = sp.get('sort_by') || 'received_at';
  const sortOrder = sp.get('sort_order') || 'desc';
  return {
    page: Number.isFinite(page) && page >= 1 ? page : 1,
    sortBy,
    sortOrder,
  };
}

export default function DashboardPage() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = readFiltersFromSearch(searchParams);
  const { page, sortBy, sortOrder } = readPagingFromSearch(searchParams);

  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(null);
  const [surveyScores, setSurveyScores] = useState(null);
  // Active date-range preset for the stat cards + pie charts.
  const [rangeKey, setRangeKey] = useState('all');

  const [data, setData] = useState({ total: 0, pages: 0, results: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // "New submissions" polling: store the total at first page load and surface
  // a banner whenever a later poll shows that the backend total has grown.
  const [initialTotal, setInitialTotal] = useState(null);
  const [newCount, setNewCount] = useState(0);

  // Row-selection state for the "Export Selected" flow. Stored as a Set of
  // submission ids. Cleared when filters change (pagination + sort do NOT
  // clear it — the user may deliberately select across pages).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Celebration trigger — fires when a status change to "resolved" succeeds.
  const [celebrate, setCelebrate] = useState(false);

  // Column visibility prefs — loaded from localStorage on mount, saved on
  // every toggle.
  const [columnVisibility, setColumnVisibility] = useState(() => loadColumnPrefs());

  function handleColumnVisibilityChange(key, visible) {
    setColumnVisibility((prev) => {
      const next = { ...prev, [key]: visible };
      saveColumnPrefs(next);
      return next;
    });
  }

  function toggleSelectOne(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllVisible() {
    const visibleIds = data.results.map((r) => r.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  // Stats + survey scores — refetched whenever the date range changes. Both
  // are scoped to the active range (All Time sends no date params). Changing
  // the range rebases the new-submission polling baseline to the current total
  // so switching ranges doesn't spuriously trip the "new submissions" banner.
  useEffect(() => {
    let cancelled = false;
    setStatsError(null);
    const params = resolveRange(rangeKey);
    getStats(params)
      .then((s) => {
        if (cancelled) return;
        setStats(s);
        setInitialTotal(s.total);
        setNewCount(0);
      })
      .catch((e) => {
        if (!cancelled) setStatsError(e.message);
      });
    getSurveyScores(params)
      .then((s) => {
        if (!cancelled) setSurveyScores(s);
      })
      .catch(() => {
        // Silent — the scores panel is supplementary, not critical.
      });
    return () => {
      cancelled = true;
    };
  }, [rangeKey]);

  // Background polling for new submissions. Lightweight — hits /stats only.
  // Errors are swallowed so a transient network blip doesn't surface a banner.
  useEffect(() => {
    if (initialTotal === null) return undefined;
    const params = resolveRange(rangeKey);
    const id = setInterval(async () => {
      try {
        const s = await getStats(params);
        if (typeof s?.total === 'number' && s.total > initialTotal) {
          setNewCount(s.total - initialTotal);
        }
      } catch {
        // Silent — try again on the next tick.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [initialTotal, rangeKey]);

  // Submissions — refetched on any param change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {
      ...filters,
      page,
      page_size: PAGE_SIZE,
      sort_by: sortBy,
      sort_order: sortOrder,
    };
    getSubmissions(params)
      .then((res) => {
        if (cancelled) return;
        setData(res);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.status,
    filters.severity,
    filters.search,
    filters.include_system,
    filters.assigned_to_name,
    filters.unassigned,
    filters.custom_assignee,
    filters.follow_up,
    page,
    sortBy,
    sortOrder,
  ]);

  const updateUrl = useCallback(
    (next) => {
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(next)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
      }
      setSearchParams(params, { replace: false });
    },
    [setSearchParams]
  );

  function handleFiltersChange(nextFilters) {
    // Resetting any filter rewinds to page 1 and clears selection — the
    // user's mental model of "the selected rows" is tied to what they can
    // currently see, so shifting filters underneath a selection is worse
    // than starting over.
    clearSelection();
    updateUrl({
      ...nextFilters,
      page: 1,
      sort_by: sortBy === 'received_at' ? undefined : sortBy,
      sort_order: sortOrder === 'desc' ? undefined : sortOrder,
    });
  }

  function handleSortChange(nextBy, nextOrder) {
    updateUrl({
      ...filters,
      page: 1,
      sort_by: nextBy,
      sort_order: nextOrder,
    });
  }

  function handlePageChange(nextPage) {
    updateUrl({
      ...filters,
      page: nextPage,
      sort_by: sortBy === 'received_at' ? undefined : sortBy,
      sort_order: sortOrder === 'desc' ? undefined : sortOrder,
    });
  }

  function handleClearFilters() {
    clearSelection();
    updateUrl({ page: 1 });
  }

  function handleQuickFilterClick(key) {
    // Clicking the currently-active quick filter clears it; clicking a
    // different one swaps to it and clears any other quick-filter axes.
    const currentlyActive = computeActiveQuickFilter(filters);
    const nextFilters = { ...filters };
    // Zero out every axis any quick filter touches, then set only the one
    // we're activating (if any).
    for (const q of QUICK_FILTERS) {
      nextFilters[q.filterKey] = undefined;
    }
    if (currentlyActive !== key) {
      const config = QUICK_FILTERS.find((q) => q.key === key);
      if (config) nextFilters[config.filterKey] = config.filterValue;
    }
    handleFiltersChange(nextFilters);
  }

  async function handleRefreshNewSubmissions() {
    // Refetch BOTH the stats and the current submissions page, then rebase
    // the baseline so the banner disappears.
    try {
      const [s, list] = await Promise.all([
        getStats(resolveRange(rangeKey)),
        getSubmissions({
          ...filters,
          page,
          page_size: PAGE_SIZE,
          sort_by: sortBy,
          sort_order: sortOrder,
        }),
      ]);
      setStats(s);
      setData(list);
      setInitialTotal(s.total);
      setNewCount(0);
    } catch (e) {
      setError(`Failed to refresh: ${e.message}`);
    }
  }

  async function handleAssigneeChange(submissionId, nextAssignee) {
    // Optimistic UI: flip the value locally, revert if the API rejects.
    setData((prev) => ({
      ...prev,
      results: prev.results.map((r) =>
        r.id === submissionId ? { ...r, assigned_to_name: nextAssignee } : r
      ),
    }));
    try {
      await updateSubmission(submissionId, { assigned_to_name: nextAssignee });
    } catch (e) {
      setError(`Failed to update assignment: ${e.message}`);
      const refreshed = await getSubmissions({
        ...filters,
        page,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setData(refreshed);
    }
  }

  async function handleStatusChange(submissionId, nextStatus) {
    // Remember the previous status so the celebration only fires on a real
    // NEW → resolved transition, not on re-selecting "resolved" for a row
    // that's already there.
    const prevStatus = data.results.find((r) => r.id === submissionId)?.status;

    // Optimistic UI update.
    setData((prev) => ({
      ...prev,
      results: prev.results.map((r) =>
        r.id === submissionId ? { ...r, status: nextStatus } : r
      ),
    }));
    try {
      await updateSubmission(submissionId, { status: nextStatus });
      if (nextStatus === 'resolved' && prevStatus !== 'resolved') {
        setCelebrate(true);
      }
    } catch (e) {
      // Revert on failure by refetching the page.
      setError(`Failed to update status: ${e.message}`);
      const refreshed = await getSubmissions({
        ...filters,
        page,
        page_size: PAGE_SIZE,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      setData(refreshed);
    }
  }

  const activeQuickFilter = computeActiveQuickFilter(filters);
  const { data: sportsData, loading: sportsLoading } = usePolling(getSportsTicker, 60000);

  // Pie data — colored, zero-value slices dropped. Intent/accomplished take
  // palette colors by order; sentiment is colored semantically by key.
  const intentData = (surveyScores?.intent?.options || [])
    .filter((o) => o.count > 0)
    .map((o, i) => ({ name: o.label, value: o.count, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const accomplishedData = (surveyScores?.accomplished?.options || [])
    .filter((o) => o.count > 0)
    .map((o, i) => ({ name: o.label, value: o.count, color: CHART_COLORS[i % CHART_COLORS.length] }));
  const sentimentData = Object.entries(stats?.by_sentiment || {})
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: SENTIMENT_LABELS[k] || k, value: v, color: SENTIMENT_COLORS[k] || '#999' }));

  return (
    <Layout
      stats={stats}
      statsLoading={!stats && !statsError}
      activeQuickFilter={activeQuickFilter}
      onQuickFilter={handleQuickFilterClick}
    >
      {newCount > 0 && (
        <button
          type="button"
          className="new-submissions-banner"
          onClick={handleRefreshNewSubmissions}
          aria-live="polite"
        >
          {newCount} new submission{newCount === 1 ? '' : 's'} received — Click to refresh
        </button>
      )}

      {statsError && <div className="form-error">{statsError}</div>}

      <div
        className="date-range-toggle"
        style={{ display: 'flex', gap: 8, margin: '4px 0 12px', flexWrap: 'wrap' }}
      >
        {DATE_RANGES.map((r) => (
          <button
            key={r.key}
            type="button"
            className={`btn btn--secondary${rangeKey === r.key ? ' btn--active' : ''}`}
            onClick={() => setRangeKey(r.key)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div
        className="pie-charts-row"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          alignItems: 'flex-start',
          marginTop: 16,
          marginBottom: 16,
        }}
      >
        <MiniPieChart
          title="What brought you to Centier.com?"
          data={intentData}
          loading={!surveyScores}
        />
        <MiniPieChart
          title="Did you accomplish what you needed?"
          data={accomplishedData}
          loading={!surveyScores}
        />
        <MiniPieChart title="Sentiment" data={sentimentData} loading={!stats} />
        <MiniScoreCircle
          title="Customer Effort Score"
          value={surveyScores?.ces?.average}
          max={5}
          n={surveyScores?.ces?.n}
          loading={!surveyScores}
        />
      </div>

      <FilterBar
        filters={filters}
        onFiltersChange={handleFiltersChange}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={handleColumnVisibilityChange}
      />

      {error && <div className="form-error">{error}</div>}

      <SubmissionTable
        submissions={data.results}
        loading={loading}
        isAdmin={isAdmin()}
        columnVisibility={columnVisibility}
        onStatusChange={handleStatusChange}
        onAssigneeChange={handleAssigneeChange}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelectOne}
        onToggleAllVisible={toggleSelectAllVisible}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        page={page}
        pageSize={PAGE_SIZE}
        total={data.total}
        pages={data.pages || 1}
        onPageChange={handlePageChange}
        onClearFilters={handleClearFilters}
      />

      <SportsTickerBar data={sportsData} loading={sportsLoading} />

      <Celebration active={celebrate} onDone={() => setCelebrate(false)} />
    </Layout>
  );
}
